import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Box,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Avatar,
  FormHelperText,
  Tooltip,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import InfoIcon from "../../../@core/components/icons/InfoIcon";

/**
 * BusinessRefundDialog Component
 * Dialog for business to refund items with options for partial/full refund
 * Shows total refund amount at bottom
 */
const BusinessRefundDialog = ({
  open,
  onClose,
  dialogData,
  onSubmit,
  totalRefundAmount = 0,
  onSelectAllItems = null, // Callback to select all items in parent
  orderData = null, // Order data to get users list
}) => {
  const [refundType, setRefundType] = useState("partial");
  const [reason, setReason] = useState("");
  const [restock, setRestock] = useState("yes");
  const [selectedUserId, setSelectedUserId] = useState("");
  const [refundMethod, setRefundMethod] = useState("");
  const [editableRefundAmount, setEditableRefundAmount] = useState(totalRefundAmount);
  const [refundAmountInput, setRefundAmountInput] = useState(totalRefundAmount.toString());
  const [showErrors, setShowErrors] = useState(false);

  // Update editable amount when totalRefundAmount prop changes
  React.useEffect(() => {
    setEditableRefundAmount(totalRefundAmount);
    setRefundAmountInput(totalRefundAmount.toString());
  }, [totalRefundAmount]);

  const handleRefundTypeChange = (event) => {
    const newType = event.target.value;
    setRefundType(newType);
    
    // If full refund is selected, trigger select all in parent
    if (newType === "full" && onSelectAllItems) {
      onSelectAllItems();
    }
  };

  const handleRestockChange = (event) => {
    setRestock(event.target.value);
  };

  const handleSubmit = () => {
    // Validate all required fields
    if (!selectedUserId || !refundMethod || editableRefundAmount <= 0) {
      setShowErrors(true);
      return;
    }

    let payload;
    
    if (refundType === "full") {
      // Full refund - refund all paid items
      const decisions = (dialogData?.selectedItemsData || []).map(decision => ({
        cartItemId: decision.cartItemId,
        refundType: decision.refundType,
        itemIdsToRefund: decision.itemIdsToRefund,
        quantityToRefund: decision.quantityToRefund,
        variantName: decision.variantName || null,
        note: decision.note || ""
      }));

      payload = {
        refundRequestType: "full_order",
        restock: restock === "yes",
        reason: reason || "",
        refundUserId: selectedUserId,
        refundAmount: editableRefundAmount,
        refundMethod: refundMethod,
        decisions: decisions
      };
    } else {
      // Partial refund - custom business decision
      // Keep decisions structure but remove refundAmount from individual decisions
      const decisions = (dialogData?.selectedItemsData || []).map(decision => ({
        cartItemId: decision.cartItemId,
        refundType: decision.refundType,
        itemIdsToRefund: decision.itemIdsToRefund,
        quantityToRefund: decision.quantityToRefund,
        variantName: decision.variantName || null,
        note: decision.note || ""
      }));

      payload = {
        refundRequestType: "partial_items",
        restock: restock === "yes",
        reason: reason || "",
        refundUserId: selectedUserId,
        refundAmount: editableRefundAmount,
        refundMethod: refundMethod,
        decisions: decisions
      };
    }
    
    onSubmit(payload, selectedUserId); // Pass selectedUserId to parent
    // keep dialog open; parent will close on successful refund
  };

  const handleClose = () => {
    setRefundType("partial");
    setReason("");
    setRestock("yes");
    setSelectedUserId("");
    setRefundMethod("");
    setEditableRefundAmount(totalRefundAmount);
    setRefundAmountInput(totalRefundAmount.toString());
    setShowErrors(false);
    onClose();
  };

  // Get users from order data - include ALL users from userOrders array
  const getAvailableUsers = () => {
    if (!orderData?.groupOrder?.userOrders || !Array.isArray(orderData.groupOrder.userOrders)) {
      return [];
    }
    
    // Get all users from userOrders array, regardless of whether they have cart items
    const usersMap = new Map();
    
    orderData.groupOrder.userOrders.forEach((userOrder, index) => {
      // Handle both cases: userId as object or userId as string ID
      let userId = null;
      let fullName = "Unknown";
      let userProfilePic = "";
      
      if (userOrder.userId) {
        if (typeof userOrder.userId === "string") {
          userId = userOrder.userId;
          fullName = userOrder.userFullName || "Unknown";
          userProfilePic = userOrder.userProfilePic || "";
        } else if (userOrder.userId._id) {
          userId = userOrder.userId._id;
          fullName = userOrder.userFullName || userOrder.userId.fullName || userOrder.userId.userName || "Unknown";
          userProfilePic = userOrder.userProfilePic || userOrder.userId.image?.profilePic || "";
        }
      }
      
      // Only add if we have a valid userId and it's not already in the map
      if (userId && !usersMap.has(userId)) {
        usersMap.set(userId, {
          _id: userId,
          fullName: fullName,
          userProfilePic: userProfilePic,
        });
      } else {
      }
    });

    // Convert map to array
    const usersArray = Array.from(usersMap.values());
    return usersArray;
  };

  const availableUsers = getAvailableUsers();

  // No default user pre-selected; placeholder is shown until user selects

  // Calculate total items and quantity from selectedItemsData
  // Count unique cartItemIds (one item can have multiple variants)
  const uniqueCartItemIds = new Set(dialogData?.selectedItemsData?.map(item => item.cartItemId) || []);
  const totalItems = uniqueCartItemIds.size || 0;
  const totalQuantity = dialogData?.selectedItemsData?.reduce((sum, item) => sum + (item.quantityToRefund || 0), 0) || 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={{
        zIndex: 10002,
      }}
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
        }
      }}
    >
      <DialogTitle sx={{ 
        borderBottom: "1px solid #E0E0E0", 
        pb: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between"
      }}>
        <Typography variant="h6" sx={{ textTransform: "lowercase" }}>
          refund
        </Typography>
        <IconButton 
          size="small" 
          onClick={handleClose}
          sx={{ ml: 2 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {/* Refund Type Selection */}
        <FormControl component="fieldset" sx={{ mb: 3, width: "100%" }}>
          <RadioGroup
            value={refundType}
            onChange={handleRefundTypeChange}
            sx={{ gap: 1 }}
          >
            <FormControlLabel
              value="partial"
              control={<Radio sx={{ color: "#7C3AED", "&.Mui-checked": { color: "#7C3AED" } }} />}
              label={
                <Typography variant="body2" sx={{ textTransform: "lowercase" }}>
                  partial refund ({totalItems} {totalItems === 1 ? 'item' : 'items'}, qty. {totalQuantity})
                </Typography>
              }
              sx={{ ml: 0 }}
            />
            <FormControlLabel
              value="full"
              control={<Radio sx={{ color: "#7C3AED", "&.Mui-checked": { color: "#7C3AED" } }} />}
              label={
                <Typography variant="body2" sx={{ textTransform: "lowercase" }}>
                  full refund (refund all paid items)
                </Typography>
              }
              sx={{ ml: 0 }}
            />
          </RadioGroup>
        </FormControl>

        {/* Editable Refund Amount */}
        <Box sx={{ mb: 3 }}>
          <Typography 
            variant="body2" 
            sx={{ 
              mb: 1, 
              textTransform: "lowercase",
              fontWeight: 500
            }}
          >
            refund amount:
          </Typography>
          <TextField
            fullWidth
            type="text"
            value={refundAmountInput}
            onChange={(e) => {
              const inputValue = e.target.value;
              // Allow empty string, numbers, and decimal point
              if (inputValue === "" || /^\d*\.?\d*$/.test(inputValue)) {
                setRefundAmountInput(inputValue);
                const numValue = parseFloat(inputValue);
                if (!isNaN(numValue) && numValue >= 0) {
                  setEditableRefundAmount(numValue);
                } else if (inputValue === "") {
                  setEditableRefundAmount(0);
                }
              }
            }}
            onBlur={() => {
              // On blur, ensure we have a valid number
              const numValue = parseFloat(refundAmountInput);
              if (isNaN(numValue) || numValue < 0) {
                setRefundAmountInput("0");
                setEditableRefundAmount(0);
              } else {
                setRefundAmountInput(numValue.toString());
                setEditableRefundAmount(numValue);
              }
            }}
            sx={{
              "& .MuiInputBase-input": {
                textAlign: "left",
              },
            }}
          />
        </Box>

        {/* Reason for Refund */}
        <Box sx={{ mb: 3 }}>
          <Typography 
            variant="body2" 
            sx={{ 
              mb: 1, 
              textTransform: "lowercase",
              fontWeight: 500
            }}
          >
            reason for refund (optional):
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="enter reason for refund..."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            sx={{
              "& .MuiInputBase-input": {
                textTransform: "lowercase",
                "&::placeholder": {
                  textTransform: "lowercase",
                  opacity: 0.7,
                },
              },
            }}
          />
        </Box>

        {/* Restock Question */}
        <FormControl component="fieldset" sx={{ mb: 3, width: "100%" }}>
          <Typography 
            variant="body2" 
            sx={{ 
              mb: 2,
              textTransform: "lowercase",
              fontWeight: 500
            }}
          >
            would you like to restock the selected items in the inventory?
          </Typography>
          <RadioGroup
            value={restock}
            onChange={handleRestockChange}
            sx={{ gap: 1 }}
          >
            <FormControlLabel
              value="yes"
              control={<Radio sx={{ color: "#7C3AED", "&.Mui-checked": { color: "#7C3AED" } }} />}
              label={
                <Typography variant="body2" sx={{ textTransform: "lowercase" }}>
                  yes
                </Typography>
              }
              sx={{ ml: 0 }}
            />
            <FormControlLabel
              value="no"
              control={<Radio sx={{ color: "#7C3AED", "&.Mui-checked": { color: "#7C3AED" } }} />}
              label={
                <Typography variant="body2" sx={{ textTransform: "lowercase" }}>
                  no
                </Typography>
              }
              sx={{ ml: 0 }}
            />
          </RadioGroup>
        </FormControl>

        {/* User Selection */}
        {availableUsers.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Select
              fullWidth
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              displayEmpty
              error={showErrors && !selectedUserId}
              renderValue={(selected) => {
                if (!selected) {
                  return (
                    <Typography sx={{ textTransform: "lowercase", color: "#000" }}>
                      select person to receive refund payment
                    </Typography>
                  );
                }
                const user = availableUsers.find(u => u._id === selected);
                return (
                  <Typography sx={{ textTransform: "lowercase", color: "#000" }}>
                    {user?.fullName || ""}
                  </Typography>
                );
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    zIndex: 10003, // Higher than dialog z-index (10002)
                  }
                },
                style: {
                  zIndex: 10003,
                }
              }}
              sx={{
                backgroundColor: showErrors && !selectedUserId ? "#ffebee" : "#E8D8FF",
                borderRadius: 2,
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: showErrors && !selectedUserId ? "#f44336" : "#E8D8FF",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: showErrors && !selectedUserId ? "#f44336" : "#E8D8FF",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: showErrors && !selectedUserId ? "#f44336" : "#E8D8FF",
                },
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  py: 2
                }
              }}
            >
              {availableUsers.map((user) => {
                return (
                  <MenuItem key={user._id} value={user._id}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography sx={{ textTransform: "lowercase" }}>
                        {user.fullName}
                      </Typography>
                    </Box>
                  </MenuItem>
                );
              })}
            </Select>
            {showErrors && !selectedUserId ? (
              <FormHelperText error sx={{ textTransform: "lowercase", ml: 2 }}>
                please select a person to receive refund payment
              </FormHelperText>
            ) : (
              <Typography sx={{ mt: 1, color: "#838383", textTransform: "lowercase", ml: 2 }}>
                *tips not included
              </Typography>
            )}
          </Box>
        )}

        {/* Refund Method Selection */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <Typography 
              variant="body2" 
              sx={{ 
                textTransform: "lowercase",
                fontWeight: 500
              }}
            >
              refund method
            </Typography>
            <Tooltip 
              title="refunds are only recorded, you must manually process refunds to your customer directly. the full amount received will reflect in your wallet and refund noted, not deducted"
              arrow
              placement="top"
              PopperProps={{
                style: {
                  zIndex: 10004,
                },
              }}
            >
              <IconButton 
                size="small" 
                sx={{ 
                  p: 0.5,
                  "&:hover": {
                    backgroundColor: "transparent"
                  }
                }}
              >
                <InfoIcon size={14} />
              </IconButton>
            </Tooltip>
          </Box>
          <Select
            fullWidth
            value={refundMethod}
            onChange={(e) => setRefundMethod(e.target.value)}
            displayEmpty
            error={showErrors && !refundMethod}
            renderValue={(selected) => {
              if (!selected) {
                return (
                  <Typography sx={{ textTransform: "lowercase", color: "#000" }}>
                    select refund method
                  </Typography>
                );
              }
              const methods = {
                card: "card",
                cash: "cash",
                app_wallet: "app/ wallet (sorry we cannot process refund via app)",
                voucher: "voucher"
              };
              return (
                <Typography sx={{ textTransform: "lowercase", color: "#000" }}>
                  {methods[selected] || selected}
                </Typography>
              );
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  zIndex: 10003, // Higher than dialog z-index (10002)
                }
              },
              style: {
                zIndex: 10003,
              }
            }}
            sx={{
              backgroundColor: showErrors && !refundMethod ? "#ffebee" : "#E8D8FF",
              borderRadius: 2,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: showErrors && !refundMethod ? "#f44336" : "#E8D8FF",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: showErrors && !refundMethod ? "#f44336" : "#E8D8FF",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: showErrors && !refundMethod ? "#f44336" : "#E8D8FF",
              },
              "& .MuiSelect-select": {
                display: "flex",
                alignItems: "center",
                gap: 1,
                py: 2
              }
            }}
          >
            <MenuItem value="card">
              <Typography sx={{ textTransform: "lowercase" }}>
                card
              </Typography>
            </MenuItem>
            <MenuItem value="cash">
              <Typography sx={{ textTransform: "lowercase" }}>
                cash
              </Typography>
            </MenuItem>
            <MenuItem value="app_wallet">
              <Typography sx={{ textTransform: "lowercase" }}>
                app/ wallet (sorry we cannot process refund via app)
              </Typography>
            </MenuItem>
            <MenuItem value="voucher">
              <Typography sx={{ textTransform: "lowercase" }}>
                voucher
              </Typography>
            </MenuItem>
          </Select>
          {showErrors && !refundMethod && (
            <FormHelperText error sx={{ textTransform: "lowercase", ml: 2 }}>
              please select a refund method
            </FormHelperText>
          )}
        </Box>

        {/* Total Refund Amount Display */}
        <Box sx={{ 
          mt: 3, 
          p: 2, 
          backgroundColor: "#f0f4ff", 
          borderRadius: 1,
          border: "1px solid #7C3AED"
        }}>
          <Typography 
            variant="body2" 
            sx={{ 
              textTransform: "lowercase",
              fontWeight: 600,
              mb: 0.5
            }}
          >
            total refund amount:
          </Typography>
          <Typography 
            variant="h6" 
            sx={{ 
              textTransform: "lowercase",
              color: "#7C3AED",
              fontWeight: 700
            }}
          >
            ${editableRefundAmount.toFixed(2)}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ borderTop: "1px solid #E0E0E0", p: 2, justifyContent: "center" }}>
        <Button 
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          sx={{ 
            mt:2,
            textTransform: "lowercase", 
            minWidth: 120,
            backgroundColor: "#7C3AED",
            "&:hover": {
              backgroundColor: "#6d28d9"
            }
          }}
        >
          refund
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BusinessRefundDialog;

